// frontend/src/util/permissions.ts
// Single, shared helper for role/privilege checks across the app.

export type EnrollmentRole = 'admin' | 'CommunityAdmin' | 'moderator' | 'member';

export type ModeratedChannel = {
  communityIdentifier: string;
  channelIdentifier: string;
  isActive?: boolean;
  role?: 'viewer' | 'moderator' | 'admin';
};

export type EnrollmentView = {
  programId?: string;
  userId?: string;
  role?: EnrollmentRole;
  communityIdentifier?: string | null;
  moderatedChannels?: ModeratedChannel[];
  visibleChannelIds?: string[];
  channels?: Array<{ channelIdentifier?: string | null }>;
  status?: { statusText?: 'active' | 'paused' | 'ended' };
  _isArchived?: boolean;
};

type Ctx = { programId?: string; communityIdentifier?: string };

const s = (v: any) => (v ?? '').toString();
const eq = (a?: string, b?: string) =>
  s(a).trim().toLowerCase() === s(b).trim().toLowerCase();
const hasValue = (value?: string | null) => s(value).trim().length > 0;
const normalizeRole = (role?: string | null) => s(role).trim().toLowerCase();
const normalizePrivacyValue = (value?: string | null) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase();

const rolePriority = {
  viewer: 0,
  member: 1,
  moderator: 2,
  admin: 3,
} as const;
const rankToRole = ['viewer', 'member', 'moderator', 'admin'] as const;

function normalizeActive(enrollments?: EnrollmentView[], ctx?: Ctx) {
  const items = Array.isArray(enrollments) ? enrollments : [];
  return items.filter((e) => {
    if (!e || e._isArchived) return false;
    if (e.status?.statusText && e.status.statusText !== 'active') return false;
    if (ctx?.programId && e.programId && !eq(e.programId, ctx.programId)) return false;
    return !!e.role;
  });
}

// Build a privilege object you can reuse in any screen
export function buildPrivileges(
  enrollments?: EnrollmentView[],
  ctx: Ctx = {},
  options: { channelsByCommunity?: Record<string, any[]> } = {}
) {
  const active = normalizeActive(enrollments, ctx);

  const platformAdmin = active.some((e) => normalizeRole(e.role) === 'admin');

  const normaliseChannelId = (id?: string) => id?.toString().split(':').pop() || '';

  const rawChannelMap = options.channelsByCommunity || {};
  const channelLookup = new Map<string, Map<string, any>>();
  const globalChannelLookup = new Map<string, Array<{ communityId: string; channel: any }>>();
  Object.entries(rawChannelMap).forEach(([communityId, channels]) => {
    const normalizedCommunity = s(communityId).trim().toLowerCase();
    if (!normalizedCommunity) return;
    const channelMap = new Map<string, any>();
    (Array.isArray(channels) ? channels : []).forEach((channel: any) => {
      const identifier = normaliseChannelId(channel?.channelIdentifier || '').trim().toLowerCase();
      if (!identifier) return;
      channelMap.set(identifier, channel);
      const existing = globalChannelLookup.get(identifier);
      if (existing) {
        existing.push({ communityId: normalizedCommunity, channel });
      } else {
        globalChannelLookup.set(identifier, [{ communityId: normalizedCommunity, channel }]);
      }
    });
    channelLookup.set(normalizedCommunity, channelMap);
  });

  const enrollmentCommunities = new Set(
    active
      .map((entry) => s(entry.communityIdentifier).trim().toLowerCase())
      .filter((value) => value.length > 0)
  );

  const getChannelInfo = (communityId: string, channelId: string) => {
    const normalizedCommunity = s(communityId).trim().toLowerCase();
    const normalizedChannel = normaliseChannelId(channelId).trim().toLowerCase();
    if (!normalizedChannel) return null;

    if (normalizedCommunity) {
      const map = channelLookup.get(normalizedCommunity);
      if (map) {
        const direct = map.get(normalizedChannel);
        if (direct) return direct;
      }
    }

    const candidates = globalChannelLookup.get(normalizedChannel);
    if (!candidates || candidates.length === 0) {
      return null;
    }

    if (normalizedCommunity) {
      const match = candidates.find((candidate) => candidate.communityId === normalizedCommunity);
      return match ? match.channel : null;
    }

    if (candidates.length === 1) {
      return candidates[0].channel;
    }

    for (const candidate of candidates) {
      if (enrollmentCommunities.has(candidate.communityId)) {
        return candidate.channel;
      }
    }

    return null;
  };

  const userIds = active
    .map((entry) => (entry.userId ? entry.userId.toString() : ''))
    .filter((value) => value.length > 0);

  const hasRole = (...roles: EnrollmentRole[]) => {
    const targets = roles.map((role) => normalizeRole(role));
    return active.some((entry) => targets.includes(normalizeRole(entry.role)));
  };

  const isCommunityAdmin = (communityId?: string) =>
    platformAdmin ||
    active.some((e) => {
      const role = normalizeRole(e.role);
      const matchesCommunity = !communityId || eq(e.communityIdentifier, communityId);
      if (!matchesCommunity) return false;
      if (role === 'communityadmin') return true;
      if (role === 'admin') {
        return hasValue(e.communityIdentifier);
      }
      return false;
    });

  const overrideRoleFor = (
    enrollment: EnrollmentView,
    communityId: string,
    channelId: string
  ): 'admin' | 'moderator' | 'viewer' | undefined => {
    const normalizedChannel = normaliseChannelId(channelId);
    const overrides = enrollment.moderatedChannels || [];
    const match = overrides.find((channel) =>
      channel &&
      channel.isActive !== false &&
      eq(channel.communityIdentifier, communityId) &&
      eq(channel.channelIdentifier, normalizedChannel)
    );
    const role = match?.role?.toString().toLowerCase();
    if (role === 'admin' || role === 'moderator' || role === 'viewer') {
      return role as 'admin' | 'moderator' | 'viewer';
    }
    return undefined;
  };

  const hasAclAccess = (communityId: string, channelId: string) => {
    const info = getChannelInfo(communityId, channelId);
    if (!info) return true;
    const privacy = info.channelPrivacySettings || {};
    const visibilityValue = normalizePrivacyValue(privacy.visibility);
    let isPrivate = false;
    if (visibilityValue === 'private') {
      isPrivate = true;
    } else if (visibilityValue === 'public') {
      isPrivate = false;
    } else {
      const typeValue = normalizePrivacyValue(privacy.type);
      isPrivate = typeValue === 'private' || typeValue === 'restricted';
    }
    if (!isPrivate) return true;

    if (platformAdmin) return true;

    const normalizedCommunity = s(communityId).trim().toLowerCase();
    const allowedIds = new Set<string>();
    if (privacy.createdByUserId) {
      allowedIds.add(privacy.createdByUserId.toString());
    }
    for (const id of privacy.allowedUserIds || []) {
      if (id) {
        allowedIds.add(id.toString());
      }
    }

    if (userIds.some((id) => allowedIds.has(id))) {
      return true;
    }

    const hasPrivilegedRole = active.some((enrollment) => {
      const role = normalizeRole(enrollment.role);
      const enrollmentCommunity = s(enrollment.communityIdentifier).trim().toLowerCase();
      const matchesCommunity = normalizedCommunity ? enrollmentCommunity === normalizedCommunity : true;
      if (role === 'admin') return true;
      if (role === 'communityadmin' && matchesCommunity) return true;
      return false;
    });

    if (hasPrivilegedRole) {
      return true;
    }

    return false;
  };

  const effectiveRoles = Array.from(
    new Set(active.map((e) => e.role).filter(Boolean))
  ) as EnrollmentRole[];

  const effectiveRoleForChannel = (
    communityId: string,
    channelId: string
  ): 'admin' | 'moderator' | 'viewer' | 'member' => {
    if (platformAdmin) return 'admin';

    if (!hasAclAccess(communityId, channelId)) {
      return 'viewer';
    }

    const normalizedCommunity = s(communityId).trim().toLowerCase();
    const normalizedChannel = normaliseChannelId(channelId);

    let baseRank = -1;
    let overrideRank: number | null = null;

    for (const enrollment of active) {
      const role = normalizeRole(enrollment.role);
      const enrollmentCommunity = s(enrollment.communityIdentifier).trim().toLowerCase();

      const matchesCommunity = normalizedCommunity ? enrollmentCommunity === normalizedCommunity : true;

      if (matchesCommunity) {
        const visibleIds = enrollment.visibleChannelIds || [];
        const hasVisibility =
          visibleIds.length === 0 ||
          visibleIds.some((id) => eq(normaliseChannelId(id), normalizedChannel));

        if (!hasVisibility && role !== 'admin') {
          // No explicit visibility for this channel; treat as hidden unless override grants access
        } else if (role === 'admin') {
          baseRank = rolePriority.admin;
          continue;
        } else if (role === 'communityadmin') {
          baseRank = Math.max(baseRank, rolePriority.admin);
        } else if (role === 'moderator') {
          baseRank = Math.max(baseRank, rolePriority.moderator);
        }
      }

      if (role === 'admin') {
        continue;
      }

      const override = overrideRoleFor(enrollment, communityId, channelId);
      if (override) {
        const rank = rolePriority[override];
        overrideRank = rank;
      }
    }

    if (overrideRank !== null) {
      return rankToRole[overrideRank] as 'viewer' | 'member' | 'moderator' | 'admin';
    }

    if (baseRank >= 0) {
      return rankToRole[baseRank] as 'viewer' | 'member' | 'moderator' | 'admin';
    }

    return 'viewer';
  };

  const isModeratorFor = (communityId: string, channelId: string) =>
    rolePriority[effectiveRoleForChannel(communityId, channelId)] >= rolePriority.moderator;

  const canManageChannelsInCommunity = (communityId: string) =>
    platformAdmin || isCommunityAdmin(communityId);

  const canEditChannel = (communityId: string, channelId: string) =>
    rolePriority[effectiveRoleForChannel(communityId, channelId)] >= rolePriority.moderator;

  const canViewChannel = (communityId: string, channelId: string) => {
    if (platformAdmin) return true;

    if (!hasAclAccess(communityId, channelId)) {
      return false;
    }

    const normalizedCommunity = s(communityId).trim().toLowerCase();
    const normalizedChannel = normaliseChannelId(channelId);

    return active.some((enrollment) => {
      const role = normalizeRole(enrollment.role);
      const enrollmentCommunity = s(enrollment.communityIdentifier).trim().toLowerCase();

      const matchesCommunity = normalizedCommunity
        ? enrollmentCommunity === normalizedCommunity
        : true;

      if (!matchesCommunity && hasValue(enrollment.communityIdentifier)) {
        return false;
      }

      if (role === 'admin') {
        return true;
      }

      const override = overrideRoleFor(enrollment, communityId, channelId);
      if (override) {
        return true;
      }

      const visibleIds = enrollment.visibleChannelIds || [];
      if (visibleIds.length === 0) {
        return true;
      }

      return visibleIds.some((id) => eq(normaliseChannelId(id), normalizedChannel));
    });
  };

  const canSeeAdminConsole = platformAdmin || active.some((e) => e.role === 'CommunityAdmin');

  const isChannelViewer = (communityId: string, channelId: string) =>
    effectiveRoleForChannel(communityId, channelId) === 'viewer';

  return {
    // tests
    platformAdmin,
    hasRole,
    isCommunityAdmin,
    isModeratorFor,
    canManageChannelsInCommunity,
    canEditChannel,
    canViewChannel,
    canSeeAdminConsole,
    effectiveRoleForChannel,
    overrideRoleFor,
    isChannelViewer,

    // data
    activeEnrollments: active,
    effectiveRoles,
  };
}

// Convenience for post-level gating against channel settings
export function canEditOrArchivePost(params: {
  enrollments?: EnrollmentView[];
  ctx: Ctx; // { programId, communityIdentifier }
  channelIdentifier: string;
  channelPostingSettings?: { canEditPosts?: Array<'admin' | 'moderator' | 'member'> } | null;
}) {
  const { enrollments, ctx, channelIdentifier, channelPostingSettings } = params;
  const p = buildPrivileges(enrollments, ctx);
  const role = p.effectiveRoleForChannel(ctx.communityIdentifier || '', channelIdentifier);
  const allowed = channelPostingSettings?.canEditPosts ?? [];
  const normalizedRole = role === 'viewer' ? 'member' : role;
  const rank = rolePriority[role as keyof typeof rolePriority] ?? 0;
  if (rank < rolePriority.moderator) return false;
  return allowed.includes(normalizedRole as 'member' | 'moderator' | 'admin');
}

// Optional: read cached active enrollment (useful if you don’t have Redux handy)
export function getActiveEnrollmentFromLS(): EnrollmentView | null {
  try {
    const raw = localStorage.getItem('activeProgramEnrollment');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Convenience: who can CREATE posts in a channel?
export function canPostToChannel(params: {
  enrollments?: EnrollmentView[];
  ctx: Ctx; // { programId, communityIdentifier }
  channelIdentifier: string;
  channelPostingSettings?: { canPost?: Array<'admin' | 'moderator' | 'member'> } | null;
}) {
  const { enrollments, ctx, channelIdentifier, channelPostingSettings } = params;
  const p = buildPrivileges(enrollments, ctx);
  const role = p.effectiveRoleForChannel(ctx.communityIdentifier || '', channelIdentifier);
  const allowed = channelPostingSettings?.canPost ?? [];
  const normalizedRole = role === 'viewer' ? 'member' : role;
  const rank = rolePriority[role as keyof typeof rolePriority] ?? 0;
  if (rank < rolePriority.moderator) return false;
  return allowed.includes(normalizedRole as 'member' | 'moderator' | 'admin');
}

// (optional) Convenience: who can COMMENT in a channel?
export function canCommentInChannel(params: {
  enrollments?: EnrollmentView[];
  ctx: Ctx; // { programId, communityIdentifier }
  channelIdentifier: string;
  channelPostingSettings?: { canComment?: Array<'admin' | 'moderator' | 'member'> } | null;
}) {
  const { enrollments, ctx, channelIdentifier, channelPostingSettings } = params;
  const p = buildPrivileges(enrollments, ctx);
  const role = p.effectiveRoleForChannel(ctx.communityIdentifier || '', channelIdentifier);
  const allowed = channelPostingSettings?.canComment ?? [];
  if (role === 'viewer') return false;
  return allowed.includes(role);
}
